import { resolveColour, type PaletteName } from "odyssey-shared";

/**
 * The radial wheel's geometry (P2.19-T11; R-0237; the design's `emotes` card, `EmoteWheelSurfaces.jsx`): a ring of
 * sectors round a hub, entry 1 centred straight up and the rest clockwise, a page's entries splitting the ring evenly.
 * Angles are degrees clockwise from straight up in the screen's axes, where y grows downward. Pure: the view and the
 * layout read these, and the ring's paint is decided here pixel by pixel so the canvas only copies colours.
 */

/**
 * The ring's measures in logical pixels, the card's: an outer radius of 96 and an inner of 54, a band of ink 4 wide
 * inside the inner radius round the hub, labels on a band 58 wide at the ring's middle; the crown of page markers 13
 * outside the ring, each 16 square, `span` degrees apart, `spanBeside` on a narrow area where the crown must close up;
 * the hub's notch reaching `notch` degrees either side of the selected sector's centre; the plaque `plaqueGap` beside
 * the ring on a narrow area.
 */
export const RING = { outer: 96, inner: 54, band: 4, labelWidth: 58, crownGap: 13, marker: 16, span: 18, spanBeside: 15, notch: 9, plaqueGap: 6 } as const;

/**
 * What a pixel of the ring is painted with: nothing, the ink edge, a sector's metal line (plain, selected, or selected
 * by the keyboard), a sector's fill (plain or selected), the hub's fill, its brass rim, and the notch on the rim that
 * points at the selected sector.
 */
export const RING_PAINT = { none: 0, edge: 1, metal: 2, metalSelected: 3, metalKeyboard: 4, fill: 5, fillSelected: 6, hubFill: 7, hubRim: 8, notch: 9 } as const;

/**
 * A paint of the ring.
 */
export type RingPaint = (typeof RING_PAINT)[keyof typeof RING_PAINT];

/**
 * Each paint's colour, the card's: the palette's names, the selected fill's own value, and null for nothing.
 */
export const RING_COLOURS: Readonly<Record<RingPaint, PaletteName | "selected-fill" | null>> = {
  [RING_PAINT.none]: null,
  [RING_PAINT.edge]: "ink-900",
  [RING_PAINT.metal]: "brass-500",
  [RING_PAINT.metalSelected]: "brass-200",
  [RING_PAINT.metalKeyboard]: "aether-300",
  [RING_PAINT.fill]: "ink-700",
  [RING_PAINT.fillSelected]: "selected-fill",
  [RING_PAINT.hubFill]: "ink-800",
  [RING_PAINT.hubRim]: "brass-300",
  [RING_PAINT.notch]: "brass-100",
};

const DEGREES = 180 / Math.PI;

/**
 * The angle of a sector's centre.
 *
 * @param index The sector.
 * @param count How many sectors the ring holds.
 * @returns Degrees clockwise from straight up: entry 1 at 0.
 * @example
 * sectorAngle(1, 4);
 * // 90
 */
export function sectorAngle(index: number, count: number): number {
  return (index * 360) / count;
}

/**
 * The angle of an offset from the centre.
 *
 * @param dx The offset across, rightward.
 * @param dy The offset down.
 * @returns Degrees clockwise from straight up, from 0 up to 360.
 */
function angleOf(dx: number, dy: number): number {
  return (Math.atan2(dx, -dy) * DEGREES + 360) % 360;
}

/**
 * The sector an angle falls in: each reaches half a step either side of its centre, so the first wraps round the top.
 *
 * @param degrees The angle, from 0 up to 360.
 * @param count How many sectors.
 * @returns The sector.
 */
function sectorOfAngle(degrees: number, count: number): number {
  const step = 360 / count;
  return Math.floor(((degrees + step / 2) % 360) / step) % count;
}

/**
 * The sector a pointer names by its direction from the centre. The hub is a dead zone and past the ring names
 * nothing, so a pointer on its way to the plaque, a marker or a page key changes nothing.
 *
 * @param dx The pointer's offset from the centre across, rightward, in logical pixels.
 * @param dy Its offset down.
 * @param count How many sectors the ring holds.
 * @param inner The ring's inner radius: closer than this is the hub.
 * @param outer The ring's outer radius: this far or further is past the ring.
 * @returns The sector, or -1 for the hub, past the ring or a ring of no sectors.
 * @example
 * sectorAt(70, 0, 4, 54, 96);
 * // 1
 */
export function sectorAt(dx: number, dy: number, count: number, inner: number, outer: number): number {
  const distance = Math.hypot(dx, dy);
  if (count <= 0 || distance < inner || distance >= outer) {
    return -1;
  }
  return sectorOfAngle(angleOf(dx, dy), count);
}

/**
 * A point at an angle and a radius from the centre, on whole logical pixels.
 *
 * @param degrees The angle, clockwise from straight up.
 * @param radius The radius.
 * @returns The offset from the centre, rounded to whole pixels.
 * @example
 * ringPoint(90, 75);
 * // { x: 75, y: 0 }
 */
export function ringPoint(degrees: number, radius: number): { readonly x: number; readonly y: number } {
  const radians = degrees / DEGREES;
  return { x: Math.round(radius * Math.sin(radians)) + 0, y: Math.round(-radius * Math.cos(radians)) + 0 };
}

/**
 * The entry an arrow steps to round the ring: Right clockwise, Left anticlockwise, wrapping at both ends.
 *
 * @param entry The entry selected.
 * @param step 1 for clockwise, -1 for anticlockwise.
 * @param count How many entries the page holds.
 * @returns The entry stepped to; the same entry on a ring of one or none.
 * @example
 * stepAround(0, -1, 4);
 * // 3
 */
export function stepAround(entry: number, step: 1 | -1, count: number): number {
  return count <= 1 ? entry : (((entry + step) % count) + count) % count;
}

/**
 * The entry nearest a direction, which Up and Down jump to: entry 1 straight up, and for Down the entry nearest
 * straight down, the first clockwise of two as near.
 *
 * @param direction Up or down.
 * @param count How many entries the page holds.
 * @returns The entry; 0 on a ring of none.
 * @example
 * nearestToward("down", 6);
 * // 3
 */
export function nearestToward(direction: "up" | "down", count: number): number {
  return direction === "up" || count <= 0 ? 0 : Math.ceil(count / 2 - 0.5);
}

/**
 * The crown's angles: a marker a page on an arc centred straight up, `span` apart, and the page keys a span past each
 * end, the key for the page before on the left.
 *
 * @param count How many pages.
 * @param span The degrees between neighbouring markers.
 * @returns The markers' angles in page order and the two keys' angles.
 * @example
 * crownAngles(5, 18);
 * // { markers: [-36, -18, 0, 18, 36], before: -54, after: 54 }
 */
export function crownAngles(count: number, span: number): { readonly markers: readonly number[]; readonly before: number; readonly after: number } {
  const markers = Array.from({ length: count }, (_, index) => (index - (count - 1) / 2) * span + 0);
  return { markers, before: (-(count + 1) / 2) * span, after: ((count + 1) / 2) * span };
}

/**
 * What one pixel of the ring is painted with, the card's drawing: the hub's fill inside a brass rim and an ink edge,
 * the notch on the rim pointing at the selected sector, the band of ink round the hub, and on the ring each sector
 * split from its neighbours by a pixel left unpainted, edged in ink, lined in metal inside that and filled.
 *
 * @param x The pixel's column on the ring's square, from its left.
 * @param y The pixel's row, from its top.
 * @param outer The outer radius; the square is twice it across.
 * @param inner The inner radius.
 * @param count How many sectors.
 * @param selected The selected sector, or -1 for none.
 * @param keyboard Whether the keyboard made the selection, which draws its metal in the focus colour.
 * @returns The paint.
 * @example
 * ringPaint(96, 96, 96, 54, 4, -1, false);
 * // RING_PAINT.hubFill
 */
export function ringPaint(x: number, y: number, outer: number, inner: number, count: number, selected: number, keyboard: boolean): RingPaint {
  const dx = x + 0.5 - outer;
  const dy = y + 0.5 - outer;
  const distance = Math.hypot(dx, dy);
  const hub = inner - RING.band;
  if (distance < hub) {
    if (selected >= 0 && count > 0 && distance >= hub - 4 && distance < hub - 1) {
      const off = Math.abs(((angleOf(dx, dy) - sectorAngle(selected, count) + 540) % 360) - 180);
      if (off < RING.notch) {
        return RING_PAINT.notch;
      }
    }
    return distance >= hub - 1 ? RING_PAINT.edge : distance >= hub - 2 ? RING_PAINT.hubRim : RING_PAINT.hubFill;
  }
  if (distance < inner) {
    return RING_PAINT.edge;
  }
  if (distance >= outer || count <= 0) {
    return RING_PAINT.none;
  }
  const step = 360 / count;
  const angle = angleOf(dx, dy);
  const sector = sectorOfAngle(angle, count);
  const start = sector * step - step / 2;
  const fromStart = (angle - start + 360) % 360;
  const edge = (distance * Math.min(fromStart, step - fromStart)) / DEGREES;
  if (edge < 1) {
    return RING_PAINT.none;
  }
  const on = sector === selected;
  if (edge < 2 || distance < inner + 1 || distance >= outer - 1) {
    return RING_PAINT.edge;
  }
  if (edge < 3 || distance < inner + 2 || distance >= outer - 2) {
    return on ? (keyboard ? RING_PAINT.metalKeyboard : RING_PAINT.metalSelected) : RING_PAINT.metal;
  }
  return on ? RING_PAINT.fillSelected : RING_PAINT.fill;
}

/**
 * Each paint's colour as red, green, blue and alpha, four bytes a paint in `RING_PAINT`'s order, nothing clear.
 */
const PAINT_RGBA: Uint8ClampedArray = (() => {
  const paints = Object.values(RING_PAINT);
  const table = new Uint8ClampedArray((Math.max(...paints) + 1) * 4);
  for (const paint of paints) {
    const colour = RING_COLOURS[paint];
    if (colour !== null) {
      const hex = resolveColour(colour);
      table.set([Number.parseInt(hex.slice(1, 3), 16), Number.parseInt(hex.slice(3, 5), 16), Number.parseInt(hex.slice(5, 7), 16), 255], paint * 4);
    }
  }
  return table;
})();

/**
 * Paints the ring into a square of pixels, red, green, blue and alpha a pixel row by row, as a canvas's image data
 * holds them: each pixel in its `ringPaint` colour, what is not painted clear. The view paints into one buffer it
 * keeps and repaints only when the selection, the page or the keyboard's hold on it changes.
 *
 * @param data The pixels, `(2 * outer) ** 2 * 4` bytes; written in place.
 * @param outer The outer radius.
 * @param inner The inner radius.
 * @param count How many sectors.
 * @param selected The selected sector, or -1 for none.
 * @param keyboard Whether the keyboard made the selection.
 * @returns `data`.
 * @throws RangeError when `data` is not the square's size.
 * @example
 * const image = context.createImageData(192, 192);
 * paintRing(image.data, 96, 54, 6, 0, false);
 * context.putImageData(image, 0, 0);
 */
export function paintRing(data: Uint8ClampedArray, outer: number, inner: number, count: number, selected: number, keyboard: boolean): Uint8ClampedArray {
  const side = 2 * outer;
  if (data.length !== side * side * 4) {
    throw new RangeError(`${data.length} bytes do not hold a ring ${side} pixels across`);
  }
  for (let y = 0; y < side; y += 1) {
    for (let x = 0; x < side; x += 1) {
      const from = ringPaint(x, y, outer, inner, count, selected, keyboard) * 4;
      const to = (y * side + x) * 4;
      data[to] = PAINT_RGBA[from] ?? 0;
      data[to + 1] = PAINT_RGBA[from + 1] ?? 0;
      data[to + 2] = PAINT_RGBA[from + 2] ?? 0;
      data[to + 3] = PAINT_RGBA[from + 3] ?? 0;
    }
  }
  return data;
}
